'use client';

import { useEffect, useRef } from 'react';
import { CloseIcon } from '@/components/aarohi/Icons';

// Garment measurements in inches (body measurements, not garment flat widths).
const APPAREL = [
  ['XS', '32', '26', '35'],
  ['S', '34', '28', '37'],
  ['M', '36', '30', '39'],
  ['L', '38', '32', '41'],
  ['XL', '40', '34', '43'],
  ['XXL', '42', '36', '45'],
];
const LEHENGA = [
  ['S', '34', '28', '42'],
  ['M', '36', '30', '42'],
  ['L', '38', '32', '42'],
  ['XL', '40', '34', '42'],
];

export default function SizeChart({ open, onClose, kind }: { open: boolean; onClose: () => void; kind: 'apparel' | 'lehenga' | 'free-size' }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  const rows = kind === 'lehenga' ? LEHENGA : APPAREL;
  const headers = kind === 'lehenga' ? ['Size', 'Bust', 'Waist', 'Lehenga length'] : ['Size', 'Bust', 'Waist', 'Hip'];

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className="m-auto w-[min(92vw,560px)] rounded-sm border border-al-line bg-al-ink-soft p-0 font-al-body text-al-cream backdrop:bg-black/70"
      aria-labelledby="size-chart-title"
    >
      <div className="flex items-center justify-between border-b border-al-line px-6 py-4">
        <h2 id="size-chart-title" className="font-al-display text-2xl">Size guide</h2>
        <button type="button" onClick={onClose} className="p-1 text-al-cream/60 hover:text-al-gold" aria-label="Close size guide">
          <CloseIcon size={20} />
        </button>
      </div>
      <div className="px-6 py-6">
        {kind === 'free-size' ? (
          <p className="text-sm leading-relaxed text-al-cream/75">
            This piece is <strong className="text-al-cream">free size</strong>. Sarees come with an unstitched blouse piece so your tailor can make the blouse to your
            measurements; dupattas are one size.
          </p>
        ) : (
          <>
            <p className="text-sm text-al-cream/65">Body measurements in inches. Between sizes? Choose the larger size.</p>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[360px] text-left text-sm tabular-nums">
                <thead>
                  <tr className="border-b border-al-line text-[11px] uppercase tracking-[0.16em] text-al-gold">
                    {headers.map((h) => <th key={h} className="py-2.5 pr-4 font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r[0]} className="border-b border-al-line/50">
                      {r.map((c, i) => <td key={i} className={`py-2.5 pr-4 ${i === 0 ? 'font-medium text-al-cream' : 'text-al-cream/75'}`}>{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {kind === 'lehenga' && (
              <p className="mt-4 text-sm text-al-cream/65">
                Lehenga blouses are <strong className="text-al-cream">semi-stitched</strong> with extra margin (fits up to 44&quot; bust) so they can be tailored to you.
              </p>
            )}
          </>
        )}
        <div className="al-rule-left mt-6" />
        <h3 className="mt-5 text-[11px] uppercase tracking-[0.18em] text-al-gold">How to measure</h3>
        <ul className="mt-3 space-y-2 text-sm text-al-cream/70">
          <li><strong className="text-al-cream">Bust:</strong> around the fullest part of your chest, tape level under the arms.</li>
          <li><strong className="text-al-cream">Waist:</strong> around your natural waistline, the narrowest part of your torso.</li>
          <li><strong className="text-al-cream">Hip:</strong> around the fullest part of your hips, feet together.</li>
        </ul>
      </div>
    </dialog>
  );
}
